<template>
    <section class="section section-shaped section-lg my-0">
        <div class="shape shape-style-1 bg-gradient-default">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
        </div>
        <div class="container pt-lg-md">
            <div class="row justify-content-center">
              <!-- Content Start -->
              <div class="col-lg-8 col-md-10 mx-auto">
                <h1 class="post-heading">The trap of superfluous “due diligence”
</h1>	

                <span class="meta"> Posted in May 2023 </span>

                <figure>
                  <img class="img-fluid" src="img/blog/card/eternal-due-diligence.jpg" alt="">
                </figure>
                
<p>Over the past 6 months we were forced to bow out of an investment in a startup on which we were particularly keen, and we barely averted a similar situation on a second one, both for the same reason.</p>
<p>The hang-up came in the due diligence phase of our investment process.</p>
<p>The term due diligence can be misleading for founders. VCs employ the term differently amongst themselves. Some venture capital investors use the term due diligence quite broadly, with the scope of their due diligence efforts encompassing nearly their full transaction process. Due diligence can begin shortly after the startup’s initial pitch meeting, and could include the VC’s entire assessment of the company: market analysis, strategic review, business model assessment, valuation benchmarking, full financial and legal audits, reference calls, and interviews with customers prospects and/or partners.</p>
<p>In contrast, I am old-school. I tend to apply a more conventional Silicon Valley view of due diligence, which is much more narrow in scope, essentially limited to the following principle: to verify that what we’ve been led to believe is indeed actually true. Specifically, in my narrow characterization, due diligence is limited to the client and reference calls as well as any detailed audits when necessary (financial/legal/technical). Everything before that is simply part of my job as a VC.</p>
<p>However, I am not writing this to complain about semantics. Either definition is fine, as is anywhere along the spectrum between my narrow usage and some investors’ broad definition of due diligence.</p>
<p>My objective here, rather, is to shed some transparency on the concept so that founders can avoid being misled, however inadvertently. Opacity on the nature and duration of a VC’s due diligence procedure does a disservice to entrepreneurs. It potentially wastes their precious fundraising time, causes them to miss out on investment from another VC fund, or in the worst case jeopardizes their company’s commercial relationships.</p>
<p>To be crystal clear and sidestep any confusion over the word due diligence, I will refrain from using it here. Instead, I will refer to an important milestone in any VC investment process: the VC term sheet.</p>
<p>The VC term sheet is a blueprint for an investment, a non-binding legal document that forms the basis of more enduring and legally binding documents. It establishes the specific conditions, valuation, and rights pertaining to the VC’s investment in the startup.</p>
<p>The term sheet is important for the startup founder because it represents a genuine intent on the part of the VC to invest. True, the term sheet is a non-binding document that subjects the contemplated transaction to certain conditions. For instance, at Shizen Capital our term sheets are conditional upon the satisfactory outcome of my narrow definition of due diligence (i.e. reference calls), as well as approval from our fund’s investment committee.</p>
<p>I submit that VC best practice should be as follows: only after the term sheet is agreed upon should the VC be allowed to perform reference checks with clients/prospects/partners of the startup. Founders may agree to make an exception to this practice, but I believe that this should come at the founder’s prerogative, and that founders should not feel pressure to do this as the norm.</p>
<p>I have witnessed too many startups in Japan fall into a trap while fundraising of overburdening their clients with reference call requests from potential investors. It is perfectly understandable to me now how this situation arose, and I do not condemn the behavior of my VC peers in Japan. The VCs are simply trying to collect as much information as possible to reassure their decision-making process. Especially in hierarchical VC fund structures, the analyst or junior associate evaluating the startup is afraid to make a career-limiting move by presenting an investment case that lacks airtight certainty.</p>
<p>The conundrum, however, is that in early-stage ventures it is impossible to eliminate all uncertainty.</p>
<p>The result is an inherent misalignment which can bring great harm to the founder. It can extend an investment process from what should take weeks into several months. Worse even, it may raise doubts among the startup’s clients, who subsequent to frequent reference calls, may start to wonder:</p>
<p>Is this startup having a difficult time fundraising ?</p>
<p>Is this startup financially stable, or should I consider switching my business to another vendor ?</p>
<p>Will being a customer of this startup always be so troublesome and time-consuming ?</p>
<p>When a prospective investor requests client calls only to decide subsequently not to invest, the startup founder has squandered a bit of their goodwill with their customers. Multiply this by several VCs, and this becomes a burden on the startup’s commercial relationships.</p>
<p>Founders should not feel obligated to grant reference calls to any candidate VC who requests them.</p>
<p>Postponing reference calls until after the term sheet is signed will separate the VCs who are genuinely interested in investing from those who are merely gathering information or embarking on fishing expeditions.</p>


              </div>
            </div>
        </div>
    </section>
</template>
<script>
export default {};
</script>
<style>
.post-heading,
.subheading,
.section-heading,
.blockquote,
.meta,
p,
span {
  color: #fff;
}
.post-heading {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}
.subheading {
  font-size: 20px;
  margin-top: 16px;
}
.section-heading {
  font-weight: 700;
  margin-top: 32px;
}
.section-heading + p {
  margin-top: 16px;
}
.meta + p {
  margin-top: 32px;
}
.author-info {
  font-size: 14px;
  font-style: italic;
  line-height: 1.5;
}
figure {
  margin-top: 24px;
}
</style>

